import { type Market, marketPrefixPattern } from '@bridebook/toolbox/src/gazetteer';
import { wrapSkimlinks } from './wrap-skimlinks';

/**
 * Articles are hosted on WordPress instance (54.78.135.105) and then copied to Bridebook.
 *
 * It is important for deep-links that internal links are relative with `_self` target.
 *
 * Processing includes:
 *  - make external links nofollow
 *  - make internal links relative (without domain, starting with /) - Wordpress IP links are also covered
 *  - prepend internal URL with market prefix if it doesn't yet have one
 *  - wrap external links with the Skimlinks link wrapper for affiliate monetisation,
 *    skipping file links (containing %2F) so encoded paths are never touched
 */

/**
 * This pattern matches anything that resembles an IPv4 address.
 */
const anyIp = /[0-9]{1,3}(?:[.][0-9]{1,3}){3}/;

/**
 * This pattern matches bridebook.com and bridebook.co.uk domains.
 * The protocol is optional, as well as the `business` or `www` subdomains.
 */
const domain = /(?:https?:[/]{2})?(?:(?:business|www)[.])?bridebook[.]co(?:m|[.]uk)/;
const domainHref = new RegExp(`href="${domain.source.replace('business|', '')}`, 'gi');
const wordpressHref = new RegExp(
  `href="https?:[/]{2}${anyIp.source}([/]${marketPrefixPattern.source})?[/](?:article[/])?`,
  'gi',
);

const urlExternal = new RegExp(`<a\\s+href="(?!(?:[/]|${domain.source}))([^"]*)"`, 'gi');
const urlInternal = new RegExp(`<a\\s+href="(?=(?:[/]|${domain.source}))`, 'gi');
const hrefWithoutLocale = new RegExp(`href="[/](?!(?:${marketPrefixPattern.source})["/])`, 'gi');

/**
 * File links (e.g. signed FS/storage URLs) contain %2F-encoded path separators.
 * These must never be wrapped, so their encoding is never touched.
 */
const isFileLink = (url: string): boolean => /%2f/i.test(url);

export const processArticleUrls = (input: string, isCordova: boolean, market: Market): string =>
  input
    .replace(domainHref, 'href="')
    .replace(wordpressHref, (_match, locale) => `href="${locale ?? ''}/article/`)
    .replace(
      urlExternal,
      (_match, url) => `<a target="_blank" href="${isFileLink(url) ? url : wrapSkimlinks(url)}"`,
    )
    .replace(urlInternal, `<a target="${isCordova ? '_self' : '_blank'}" href="`)
    .replace(hrefWithoutLocale, `href="/${market.prefix}/`);
